import { styled } from '@linaria/react'
import { MinimalButton, MinimalCard, MinimalInput } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'

/**
 * Console chrome.
 *
 * The console is a desk tool, not the phone app: wide tables, dense rows, no
 * animation. It still draws every colour, radius, and type size from the shared
 * theme, so it inherits light/dark and any brand change along with the rest of
 * the product — the layout differs, the design language does not.
 */

export const Page = styled.div`
  /* The page is its own scroll container. styles/appReset.ts pins html, body
     and #root to the viewport with overflow hidden (the app shell scrolls
     #main-content instead), so a page that relies on document scroll cannot
     scroll at all. The sticky header sticks to this box. */
  height: 100dvh;
  width: 100%;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: ${minimalVars.color.bgApp};
  color: ${minimalVars.color.textPrimary};
`

export const Bar = styled.header`
  display: flex;
  align-items: center;
  gap: ${minimalVars.space.sm};
  padding: ${() => `${minimalVars.space.xs} ${minimalVars.space.sm}`};
  border-bottom: 1px solid ${minimalVars.color.borderSubtle};
  background: ${minimalVars.color.bgSurface};
  position: sticky;
  top: 0;
  z-index: 10;
  flex-wrap: wrap;
  box-sizing: border-box;
  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: ${minimalVars.space['2xs']};
    padding-top: calc(${minimalVars.space.xs} + env(safe-area-inset-top, 0px));
    > span:first-child { grid-column: 1; grid-row: 1; }
    > span:last-of-type { grid-column: 1; grid-row: 2; }
    > div { display: none; }
    > button { grid-column: 2; grid-row: 1 / span 2; }
    nav { grid-column: 1 / -1; grid-row: 3; min-width: 0; flex-wrap: nowrap; overflow-x: auto; }
    nav button { flex-shrink: 0; }
  }
`

export const Brand = styled.span`
  font-weight: ${minimalVars.typography.weightBold};
  letter-spacing: -0.01em;
`

export const BrandMark = styled.span`
  color: ${minimalVars.color.brand};
`

export const Nav = styled.nav`
  display: flex;
  gap: ${minimalVars.space['3xs']};
  flex-wrap: wrap;
`

export const NavLink = styled(MinimalButton)<{ $active: boolean }>`
  border: 0;
  background: ${({ $active }) => ($active ? minimalVars.color.brandSoft : 'transparent')};
  color: ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.textSecondary)};
  font: inherit;
  font-size: ${minimalVars.typography.captionSize};
  font-weight: ${minimalVars.typography.weightSemibold};
  padding: ${minimalVars.space['2xs']} ${minimalVars.space.xs};
  min-height: 44px;
  border-radius: ${minimalVars.radius.pill};
  cursor: pointer;
  &:hover {
    color: ${minimalVars.color.brand};
  }
  &:focus-visible {
    outline: 2px solid ${minimalVars.color.brand};
    outline-offset: 2px;
  }
`

export const Spacer = styled.div`
  margin-left: auto;
`

export const Who = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
`

export const Main = styled.main`
  padding: ${minimalVars.space.sm};
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  > * { min-width: 0; }
  gap: ${minimalVars.space.sm};
  max-width: 1200px;
  margin: 0 auto;
`

export const H1 = styled.h1`
  margin: 0;
  font-size: ${minimalVars.typography.h2Size};
  font-weight: ${minimalVars.typography.weightBold};
  letter-spacing: -0.02em;
  font-family: ${minimalVars.typography.displayFamily};
`

export const Lede = styled.p`
  margin: ${minimalVars.space['2xs']} 0 0;
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.bodySize};
`

export const Card = styled(MinimalCard)`
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: ${minimalVars.space.sm};
  > div { display: grid; gap: ${minimalVars.space.xs}; min-width: 0; }
`

export const CardTitle = styled.h2`
  margin: 0 0 ${minimalVars.space.xs};
  font-size: ${minimalVars.typography.h2Size};
  font-weight: ${minimalVars.typography.weightSemibold};
`

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 150px), 1fr));
  gap: ${minimalVars.space.xs};
`

export const Form = styled.form`
  display: grid;
  gap: ${minimalVars.space.sm};
  min-width: 0;
`

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: ${minimalVars.space.xs};
  > * { min-width: 0; }
  input { min-width: 0; max-width: 100%; box-sizing: border-box; }
  align-items: start;
  margin-block: ${minimalVars.space.xs};
`

export const Stat = styled.div`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: ${minimalVars.space.xs};
`

export const StatButton = styled.button`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: ${minimalVars.space.xs};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  min-width: 0;
  overflow-wrap: anywhere;
  &[aria-pressed="true"] { border-color: ${minimalVars.color.brand}; background: ${minimalVars.color.brandSoft}; }
  &:hover { border-color: ${minimalVars.color.brand}; }
  &:focus-visible { outline: 2px solid ${minimalVars.color.brand}; outline-offset: 2px; }
`

export const StatValue = styled.div`
  font-size: ${minimalVars.typography.h2Size};
  font-weight: ${minimalVars.typography.weightBold};
  line-height: 1.1;
`

export const StatLabel = styled.div`
  margin-top: 2px;
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
`

/* Wide tables scroll inside their own container; the page never scrolls
   sideways. */
export const TableScroll = styled.div`
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${minimalVars.typography.captionSize};
  th,
  td {
    text-align: left;
    /* 8px exact on the space scale; 10px is off scale, so the shorthand stays raw. */
    padding: 8px 10px;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    white-space: nowrap;
  }
  th {
    color: ${minimalVars.color.textTertiary};
    font-weight: ${minimalVars.typography.weightSemibold};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    /* 11px sits below the 12px metaSize floor for this dense uppercase header. */
    font-size: 11px;
  }
  td.wrap {
    white-space: normal;
    min-width: 220px;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
`

export const Pill = styled.span<{ $tone: 'ok' | 'warn' | 'bad' | 'flat' }>`
  display: inline-block;
  padding: ${minimalVars.space['3xs']} ${minimalVars.space.xs};
  border-radius: ${minimalVars.radius.pill};
  /* 11px keeps status pills inside a table cell without wrapping the label. */
  font-size: 11px;
  font-weight: ${minimalVars.typography.weightSemibold};
  background: ${({ $tone }) =>
    $tone === 'ok'
      ? minimalVars.color.successSoft
      : $tone === 'warn'
        ? minimalVars.color.warningSoft
        : $tone === 'bad'
          ? minimalVars.color.dangerSoft
          : minimalVars.color.bgSurfaceAlt};
  color: ${({ $tone }) =>
    $tone === 'ok'
      ? minimalVars.color.success
      : $tone === 'warn'
        ? minimalVars.color.warning
        : $tone === 'bad'
          ? minimalVars.color.danger
          : minimalVars.color.textSecondary};
`

export const Action = styled(MinimalButton)`
  min-height: 44px;
`

export const Danger = styled(Action)`
  &:hover:not(:disabled) {
    border-color: ${minimalVars.color.danger};
    color: ${minimalVars.color.danger};
  }
`

export const Empty = styled.p`
  margin: 0;
  padding: ${minimalVars.space.sm} 0;
  color: ${minimalVars.color.textTertiary};
  font-size: ${minimalVars.typography.captionSize};
  text-align: center;
`

export const Note = styled.p`
  margin: ${minimalVars.space.xs} 0 0;
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
`

export const ErrorNote = styled(Note)`
  padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
  border-radius: ${minimalVars.radius.sm};
  background: ${minimalVars.color.dangerSoft};
  color: ${minimalVars.color.danger};
  line-height: 1.45;
  overflow-wrap: anywhere;
`

export const Field = styled.label`
  min-width: 0;
  display: grid;
  gap: ${minimalVars.space['2xs']};
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textSecondary};
  font-weight: ${minimalVars.typography.weightSemibold};
`

export const Input = MinimalInput

export const LoginWrap = styled.div`
  height: 100dvh;
  overflow-y: auto;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  padding: ${minimalVars.space.sm};
  background: ${minimalVars.color.bgApp};
`

export const LoginCard = styled.form`
  width: min(440px, 100%);
  min-width: 0;
  box-sizing: border-box;
  /* Bespoke panel shadow. The shadow scale is tuned separately, so it stays raw. */
  box-shadow: var(--chow-shadow-panel);
  display: grid;
  gap: ${minimalVars.space.xs};
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: ${minimalVars.space.sm};
`

export const Submit = styled.button`
  font: inherit;
  font-weight: ${minimalVars.typography.weightSemibold};
  /* 10px and 14px are off scale, so this padding stays raw to keep button height. */
  padding: 10px 14px;
  border: 0;
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.brand};
  color: ${minimalVars.color.textInverse};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

/** A vertical group of radio choices with room to tap each one. */
export const Choice = styled.div`
  display: grid;
  gap: ${minimalVars.space.xs};
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textPrimary};
  & label {
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    min-height: 36px;
  }
`
